import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLoans, formatCurrency } from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import LoanStatusNotices from '../components/LoanStatusNotices';
import { overdueBadgeFor } from '../utils/lateFeeDisplay';

function Dashboard() {
  const { user } = useAuth();
  // Deliberately NOT getLateFee()/getNoticeFee(): on a tenant site those return
  // a hardcoded client constant, and a late fee is a property of the LOAN in
  // any case. Every figure in the overdue notices comes off the loan row.
  const { getCompanyLegalName, getCompanyEmail, getCompanyPhone } = useConfig();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLoans();
  }, []);

  const loadLoans = async () => {
    try {
      const response = await getLoans();
      setLoans(response.data);
    } catch (err) {
      setError('Failed to load loans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadContract = async (loanId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/loans/${loanId}/contract`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch (err) {
      console.error('Load contract error:', err);
      return null;
    }
  };

  const signContract = async (loanId, signature) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/loans/${loanId}/sign-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ signature })
      });

      if (!response.ok) throw new Error('Failed to sign');

      const data = await response.json();
      alert(data.message);
      loadLoans();
    } catch (err) {
      alert('Failed to sign contract');
    }
  };

  const downloadContract = async (loanId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/loans/${loanId}/download-contract`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contract_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download contract');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <h1>Welcome back<br />{user.firstName}!</h1>
      
      <div className="dashboard-summary">
        <div className="summary-card">
          <h3>Active Loans</h3>
          <p className="summary-number">{loans.filter(l => l.status === 'active').length}</p>
        </div>
        <div className="summary-card">
          <h3>Total Properties</h3>
          <p className="summary-number">{loans.filter(l => l.status !== 'defaulted').length}</p>
        </div>
        <div className="summary-card">
          <h3>Paid Off</h3>
          <p className="summary-number">{loans.filter(l => l.status === 'paid_off').length}</p>
        </div>
      </div>

      <h2>Your Properties</h2>
      
      {loans.filter(l => l.status !== 'defaulted').length === 0 ? (
        <div className="empty-state">
          <p>You don't have any properties yet.</p>
          <Link to="/properties" className="btn btn-primary">
            Browse Available Properties
          </Link>
        </div>
      ) : (
        <div className="loans-grid">
          {loans.filter(l => l.status !== 'defaulted').map(loan => {
            const percentPaid = ((parseFloat(loan.loan_amount) - parseFloat(loan.balance_remaining)) / parseFloat(loan.loan_amount)) * 100;
            const paymentsMade = parseInt(loan.payments_made) || 0;
            const remainingPayments = Math.max(0, parseInt(loan.term_months) - paymentsMade);
            
            // Overdue status is the SERVER's answer, delivered on the loan row.
            // This screen used to work it out from next_payment_date with its own
            // Math.ceil day count -- which ran a full day ahead of the server's --
            // and its own hardcoded grace period. See utils/lateFeeDisplay.js.
            const paymentStatus = overdueBadgeFor(loan);
            
            return (
              <div key={loan.id} className="loan-card">
                {/* Contract Section - Shows all three states */}

                {/* State 1: Awaiting Admin Signature - Client can view but not sign yet */}
                {loan.contract_status === 'pending_admin_signature' && (
                  <div className="contract-pending-box" style={{ backgroundColor: 'var(--color-warning-bg)', borderLeft: '4px solid var(--color-warning-border)' }}>
                    <div className="contract-header">
                      <span className="contract-icon">⏳</span>
                      <strong className="contract-title" style={{ color: 'var(--color-warning-text)' }}>Awaiting Admin Signature</strong>
                    </div>
                    <p className="contract-text" style={{ color: 'var(--color-warning-text)' }}>
                      Your contract has been generated and is awaiting signature from {getCompanyLegalName()}. You can review it below.
                    </p>
                    <button
                      onClick={async () => {
                        const contract = await loadContract(loan.id);
                        if (!contract) {
                          alert('Failed to load contract');
                          return;
                        }

                        // Show contract in read-only mode - SECURE: Using DOM API to prevent XSS
                        const modal = document.createElement('div');
                        modal.className = 'contract-modal-overlay';

                        const container = document.createElement('div');
                        container.className = 'contract-modal-container';

                        const header = document.createElement('div');
                        header.className = 'contract-modal-header';
                        const h2 = document.createElement('h2');
                        h2.textContent = '📄 Contract for Deed - Preview';
                        header.appendChild(h2);

                        const body = document.createElement('div');
                        body.className = 'contract-modal-body';
                        body.textContent = contract.contract_text; // SAFE: textContent escapes HTML/JS

                        const footer = document.createElement('div');
                        footer.className = 'contract-modal-footer';

                        const notice = document.createElement('div');
                        notice.style.cssText = 'background:#fff3cd;padding:15px;border-radius:5px;margin-bottom:15px;border:2px solid #ffc107;color:#856404;';
                        const noticeStrong = document.createElement('strong');
                        noticeStrong.textContent = 'ℹ️ Awaiting Admin Signature';
                        notice.appendChild(noticeStrong);
                        notice.appendChild(document.createElement('br'));
                        notice.appendChild(document.createTextNode(`This contract is awaiting signature from ${getCompanyLegalName()}. Once signed by the seller, you will be able to review and sign.`));

                        const closeBtn = document.createElement('button');
                        closeBtn.className = 'btn-cancel-modal';
                        closeBtn.textContent = 'Close';
                        closeBtn.style.cssText = 'width:100%;';

                        footer.appendChild(notice);
                        footer.appendChild(closeBtn);

                        container.appendChild(header);
                        container.appendChild(body);
                        container.appendChild(footer);
                        modal.appendChild(container);

                        document.body.appendChild(modal);

                        closeBtn.onclick = () => {
                          document.body.removeChild(modal);
                        };

                        modal.onclick = (e) => {
                          if (e.target === modal) {
                            document.body.removeChild(modal);
                          }
                        };
                      }}
                      className="btn contract-button"
                      style={{
                        backgroundColor: '#6c757d',
                        color: 'white'
                      }}
                    >
                      📄 View Contract
                    </button>
                  </div>
                )}

                {/* State 2: Ready for Customer Signature */}
                {loan.contract_status === 'pending_client_signature' && (
                  <div className="contract-pending-box">
                    <div className="contract-header">
                      <span className="contract-icon">📝</span>
                      <strong className="contract-title">Contract Ready to Sign</strong>
                    </div>
                    <p className="contract-text">
                      Your Contract for Deed is ready for review and signature.
                    </p>
                    <button
                      onClick={async () => {
                        const contract = await loadContract(loan.id);
                        if (contract) {
                          // Show contract modal - SECURE: Using DOM API instead of innerHTML to prevent XSS
                          const modal = document.createElement('div');
                          modal.className = 'contract-modal-overlay';

                          const container = document.createElement('div');
                          container.className = 'contract-modal-container';

                          const header = document.createElement('div');
                          header.className = 'contract-modal-header';
                          const h2 = document.createElement('h2');
                          h2.textContent = 'Contract for Deed - Review';
                          header.appendChild(h2);

                          const body = document.createElement('div');
                          body.className = 'contract-modal-body';
                          body.textContent = contract.contract_text; // SAFE: textContent escapes HTML/JS

                          const footer = document.createElement('div');
                          footer.className = 'contract-modal-footer';

                          const notice = document.createElement('div');
                          notice.className = 'signature-agreement-notice';
                          const noticeStrong = document.createElement('strong');
                          noticeStrong.textContent = '⚠️ Electronic Signature Agreement';
                          notice.appendChild(noticeStrong);
                          notice.appendChild(document.createElement('br'));
                          notice.appendChild(document.createTextNode('By typing your name below, you agree to sign this Contract for Deed electronically. This signature will be legally binding.'));

                          const input = document.createElement('input');
                          input.type = 'text';
                          input.id = 'signatureInput';
                          input.className = 'signature-input';
                          input.placeholder = 'Type your full legal name here';

                          const buttonGroup = document.createElement('div');
                          buttonGroup.className = 'modal-button-group';

                          const signBtn = document.createElement('button');
                          signBtn.id = 'signBtn';
                          signBtn.className = 'btn-sign';
                          signBtn.textContent = '✍️ Sign Contract';

                          const cancelBtn = document.createElement('button');
                          cancelBtn.id = 'cancelBtn';
                          cancelBtn.className = 'btn-cancel-modal';
                          cancelBtn.textContent = 'Cancel';

                          buttonGroup.appendChild(signBtn);
                          buttonGroup.appendChild(cancelBtn);

                          footer.appendChild(notice);
                          footer.appendChild(input);
                          footer.appendChild(buttonGroup);

                          container.appendChild(header);
                          container.appendChild(body);
                          container.appendChild(footer);
                          modal.appendChild(container);

                          document.body.appendChild(modal);
                          
                          document.getElementById('cancelBtn').onclick = () => {
                            document.body.removeChild(modal);
                          };
                          
                          document.getElementById('signBtn').onclick = async () => {
                            const signature = document.getElementById('signatureInput').value.trim();
                            if (!signature) {
                              alert('Please enter your full legal name');
                              return;
                            }
                            document.body.removeChild(modal);
                            await signContract(loan.id, signature);
                          };
                        }
                      }}
                      className="btn contract-button"
                      style={{
                        backgroundColor: 'var(--forest-green)',
                        color: 'white'
                      }}
                    >
                      📝 Review & Sign Contract
                    </button>
                  </div>
                )}

                {/* State 3: Fully Executed Contract */}
                {loan.contract_status === 'fully_executed' && (
                  <div className="contract-complete-box">
                    <div className="contract-header">
                      <span className="contract-icon">✅</span>
                      <strong className="contract-title" style={{ color: 'var(--color-success)' }}>Contract Fully Executed</strong>
                    </div>
                    <p className="contract-text">
                      Both parties have signed. Your contract is complete.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={async () => {
                          const contract = await loadContract(loan.id);
                          if (!contract) {
                            alert('Failed to load contract');
                            return;
                          }

                          // Show signed contract modal - SECURE: Using DOM API to prevent XSS
                          const modal = document.createElement('div');
                          modal.className = 'contract-modal-overlay';

                          const container = document.createElement('div');
                          container.className = 'contract-modal-container';

                          const header = document.createElement('div');
                          header.className = 'contract-modal-header';
                          const h2 = document.createElement('h2');
                          h2.textContent = '📄 Signed Contract for Deed';
                          header.appendChild(h2);

                          const body = document.createElement('div');
                          body.className = 'contract-modal-body';
                          body.textContent = contract.contract_text; // SAFE: textContent escapes HTML/JS

                          const footer = document.createElement('div');
                          footer.className = 'contract-modal-footer';

                          // Signatures section
                          const signaturesSection = document.createElement('div');
                          signaturesSection.style.cssText = 'background:#f8f9fa;padding:20px;border-radius:8px;margin-bottom:20px;border:2px solid #28a745;';

                          const sigTitle = document.createElement('h3');
                          sigTitle.style.cssText = 'margin:0 0 15px 0;color:#28a745;font-size:18px;';
                          sigTitle.textContent = '✍️ Electronic Signatures';
                          signaturesSection.appendChild(sigTitle);

                          // Admin signature
                          const adminSigDiv = document.createElement('div');
                          adminSigDiv.style.cssText = 'margin-bottom:15px;padding:15px;background:white;border-radius:5px;border-left:4px solid var(--forest-green);';

                          const adminLabel = document.createElement('strong');
                          adminLabel.textContent = `Seller (${getCompanyLegalName()}):`;
                          adminSigDiv.appendChild(adminLabel);
                          adminSigDiv.appendChild(document.createElement('br'));

                          const adminSigText = document.createElement('span');
                          adminSigText.style.cssText = 'font-family:cursive;font-size:20px;color:#333;';
                          adminSigText.textContent = contract.admin_signature || 'Not signed';
                          adminSigDiv.appendChild(adminSigText);
                          adminSigDiv.appendChild(document.createElement('br'));

                          const adminDate = document.createElement('small');
                          adminDate.style.cssText = 'color:#666;';
                          adminDate.textContent = contract.admin_signed_date
                            ? `Signed on: ${new Date(contract.admin_signed_date).toLocaleString()}`
                            : '';
                          adminSigDiv.appendChild(adminDate);

                          // Customer signature
                          const customerSigDiv = document.createElement('div');
                          customerSigDiv.style.cssText = 'padding:15px;background:white;border-radius:5px;border-left:4px solid #007bff;';

                          const customerLabel = document.createElement('strong');
                          customerLabel.textContent = 'Buyer (Customer):';
                          customerSigDiv.appendChild(customerLabel);
                          customerSigDiv.appendChild(document.createElement('br'));

                          const customerSigText = document.createElement('span');
                          customerSigText.style.cssText = 'font-family:cursive;font-size:20px;color:#333;';
                          customerSigText.textContent = contract.customer_signature || 'Not signed';
                          customerSigDiv.appendChild(customerSigText);
                          customerSigDiv.appendChild(document.createElement('br'));

                          const customerDate = document.createElement('small');
                          customerDate.style.cssText = 'color:#666;';
                          customerDate.textContent = contract.customer_signed_date
                            ? `Signed on: ${new Date(contract.customer_signed_date).toLocaleString()}`
                            : '';
                          customerSigDiv.appendChild(customerDate);

                          signaturesSection.appendChild(adminSigDiv);
                          signaturesSection.appendChild(customerSigDiv);

                          // Close button
                          const closeBtn = document.createElement('button');
                          closeBtn.className = 'btn-cancel-modal';
                          closeBtn.textContent = 'Close';
                          closeBtn.style.cssText = 'width:100%;';

                          footer.appendChild(signaturesSection);
                          footer.appendChild(closeBtn);

                          container.appendChild(header);
                          container.appendChild(body);
                          container.appendChild(footer);
                          modal.appendChild(container);

                          document.body.appendChild(modal);

                          closeBtn.onclick = () => {
                            document.body.removeChild(modal);
                          };

                          // Close on overlay click
                          modal.onclick = (e) => {
                            if (e.target === modal) {
                              document.body.removeChild(modal);
                            }
                          };
                        }}
                        className="btn contract-button"
                        style={{
                          backgroundColor: 'var(--forest-green)',
                          color: 'white',
                          flex: '1'
                        }}
                      >
                        👁️ View Signed Contract
                      </button>
                      <button
                        onClick={() => downloadContract(loan.id)}
                        className="btn contract-button"
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          flex: '1'
                        }}
                      >
                        📄 Download PDF
                      </button>
                    </div>
                  </div>
                )}

                {/* Overdue status, late fee and any served notice — all of it
                    rendered from the loan's own server-computed flags. This is
                    the block that used to say "A $75 late fee has been applied"
                    from a hardcoded 8-day threshold. */}
                <LoanStatusNotices
                  loan={loan}
                  companyPhone={getCompanyPhone()}
                  companyEmail={getCompanyEmail()}
                />

                {paymentStatus && (
                  <div className="payment-status-badge" style={{ backgroundColor: paymentStatus.color }}>
                    {paymentStatus.icon} {paymentStatus.text}
                  </div>
                )}

                <h3>{loan.property_title}</h3>
                <p className="loan-location">{loan.location}</p>
                
                <div className="loan-details">
                  <div className="loan-detail-row">
                    <span>Status:</span>
                    <span className={`status-badge status-${loan.status}`}>
                      {loan.status === 'active' ? 'Active' : 'Paid Off'}
                    </span>
                  </div>
                  
                  {loan.next_payment_date && loan.status === 'active' && (
                    <div className="loan-detail-row">
                      <span>Next Payment Due:</span>
                      <span style={{ fontWeight: '600', color: 'var(--color-brand-text)' }}>
                        {new Date(loan.next_payment_date.split('T')[0].replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  
                  <div className="loan-detail-row">
                    <span>Remaining Balance:</span>
                    <span className="loan-amount">
                      ${formatCurrency(loan.balance_remaining)}
                    </span>
                  </div>
                  
                  <div className="loan-detail-row">
                    <span>Payments Remaining:</span>
                    <span>{remainingPayments}</span>
                  </div>
                  
                  <div className="loan-detail-row">
                    <span>Status:</span>
                    <span className={`status-badge status-${loan.status}`}>
                      {loan.status === 'active' ? 'Active' : 'Paid Off'}
                    </span>
                  </div>
                </div>

                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${Math.min(percentPaid, 100)}%` }}
                  ></div>
                </div>
                <p className="progress-text">{Math.round(percentPaid)}% Paid</p>

                <Link 
                  to={`/loans/${loan.id}`} 
                  className="btn btn-secondary btn-full-width"
                >
                  View Details & Make Payment
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dashboard;